import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { LangContext } from '../App';
import { translations } from '../lib/translations';

const MONOGRAM_URL =
  'https://customer-assets.emergentagent.com/job_bbc14d05-1309-4872-bdcf-991a413c9c8e/artifacts/9i1h196a_JCS_monograma_transparente.png';

const JESSICA_PHOTO =
  'https://customer-assets.emergentagent.com/job_jessica-costa-law/artifacts/1owtmzmj_WhatsApp%20Image%202026-07-15%20at%2011.06.11.jpeg';

export default function Hero() {
  const lang = useContext(LangContext);
  const t = translations[lang].hero;

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen bg-[#FDFBF7] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* ── Photo: full background on mobile, right panel on desktop ── */}
      <motion.div
        className="absolute inset-0 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[58%]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, ease: 'easeOut', delay: 0.1 }}
        data-testid="hero-photo-container"
      >
        <img
          src={JESSICA_PHOTO}
          alt="Dra. Jéssica Costa da Silva — Advogada"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center 8%' }}
          data-testid="hero-photo"
        />

        {/* Mobile: strong overlay so text remains readable */}
        <div
          className="absolute inset-0 lg:hidden"
          style={{
            background:
              'linear-gradient(to right, rgba(253,251,247,0.92) 0%, rgba(253,251,247,0.88) 45%, rgba(253,251,247,0.55) 75%, rgba(253,251,247,0.25) 100%)',
          }}
        />

        {/* Desktop: left-edge fade into cream */}
        <div
          className="absolute inset-0 hidden lg:block"
          style={{
            background:
              'linear-gradient(to right, #FDFBF7 0%, #FDFBF7 8%, rgba(253,251,247,0.9) 22%, rgba(253,251,247,0.5) 40%, rgba(253,251,247,0.08) 60%, transparent 72%)',
          }}
        />

        {/* Bottom fade — blends into next section */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, #FDFBF7 0%, rgba(253,251,247,0.5) 8%, transparent 22%)',
          }}
        />

        {/* Top fade — blends with navbar */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, #FDFBF7 0%, transparent 14%)',
          }}
        />
      </motion.div>

      {/* ── Watermark monogram (behind everything) ── */}
      <div className="absolute inset-0 flex items-end justify-start pointer-events-none select-none overflow-hidden">
        <img
          src={MONOGRAM_URL}
          alt=""
          aria-hidden="true"
          className="w-[480px] opacity-[0.04] -translate-x-16 translate-y-16"
        />
      </div>

      {/* ── Text content ── */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 w-full pt-24 pb-20 min-h-screen flex items-center">
        <div className="max-w-md">

          <motion.p
            className="font-sans text-xs tracking-[0.35em] uppercase text-[#6B2D3E] mb-8"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            — {t.subtitle} —
          </motion.p>

          <motion.h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.05] tracking-tight mb-8"
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          >
            Jéssica<br />
            <em>Costa</em><br />
            da Silva
          </motion.h1>

          <motion.div
            className="w-12 h-px bg-[#6B2D3E] mb-8"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            style={{ transformOrigin: 'left' }}
            transition={{ duration: 0.7, delay: 0.65 }}
          />

          <motion.p
            className="font-sans text-base text-[#4A4A4A] leading-relaxed max-w-xs mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            {t.tagline}
          </motion.p>

          <motion.button
            onClick={() => scrollTo('about')}
            data-testid="hero-cta-btn"
            className="font-sans text-xs tracking-[0.25em] uppercase text-[#6B2D3E] border border-[#6B2D3E] px-8 py-3.5 w-fit hover:bg-[#6B2D3E] hover:text-white transition-all duration-300 active:scale-95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            {t.cta}
          </motion.button>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer z-10"
        onClick={() => scrollTo('about')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        data-testid="scroll-indicator"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown size={20} className="text-[#6B2D3E] opacity-50" />
        </motion.div>
      </motion.div>
    </section>
  );
}
